import { useCallback } from "react"
import { FileStatusDisplayIdEnum } from "./schema"
import { DataStatus } from "../enums"
import {
  bannersReset,
  errorHandlerUpdated,
} from "../../features/errors/errorSlice"
import { useAppDispatch } from "../../reduxStore/hooks"
import { useCMSReady } from "../CMS/useCMSReady"

type FileResponse<T> = {
  status: FileStatusDisplayIdEnum
  data?: T
}

interface DownloadSignedDocumentHookProps<T> {
  fileApiCall: () => Promise<FileResponse<T>>
  setDownloadStatus?: (status: DataStatus) => void
  setFileProcessing?: (value: boolean) => void
}

/**
 * A custom hook for fetching and downloading document blobs.
 *
 * @template T The type of data returned by the file API call.
 * @param {Object} params - The parameters for the hook.
 * @param {() => Promise<FileResponse<T>>} params.fileApiCall - The function to call the file API.
 * @param {(value: boolean) => void} params.setFileProcessing - A function to set the file processing state.
 * @param {(status: DataStatus) => void} params.setDownloadStatus - A function to set the download status.
 * @returns {() => Promise<T | undefined>} A function that when called, initiates the document download process.
 */
export const useFetchDocumentBlob = <T>({
  fileApiCall,
  setDownloadStatus,
  setFileProcessing,
}: DownloadSignedDocumentHookProps<T>) => {
  const dispatch = useAppDispatch()
  const { cms } = useCMSReady(["common"])
  const { common: commonText } = cms
  const downloadSignedDocument = useCallback(async () => {
    dispatch(bannersReset())
    setFileProcessing?.(false)
    setDownloadStatus?.(DataStatus.SENDING)

    try {
      const fileResponse = await fileApiCall()

      if (fileResponse.status === FileStatusDisplayIdEnum.AVAILABLE) {
        setDownloadStatus?.(DataStatus.LOADED)
        return fileResponse.data
      } else if (fileResponse.status === FileStatusDisplayIdEnum.PROCESSING) {
        setDownloadStatus?.(DataStatus.LOADED)
        dispatch(
          errorHandlerUpdated({
            infoBanner: {
              isVisible: true,
              title: "Warning",
              content: commonText?.fileProcessing || "",
            },
          }),
        )
        setFileProcessing?.(true)
      } else {
        setDownloadStatus?.(DataStatus.ERROR)
        dispatch(
          errorHandlerUpdated({
            errorBanner: {
              isVisible: true,
              title: "Error",
              content: commonText?.fileDownloadError || "",
            },
          }),
        )
        console.error("Failed to download document.")
      }
    } catch (error) {
      setDownloadStatus?.(DataStatus.ERROR)
      dispatch(
        errorHandlerUpdated({
          errorBanner: {
            isVisible: true,
            title: "Error",
            content: commonText?.fileDownloadError || "",
          },
        }),
      )
      console.error(`Failed to download file: ${error}`)
    }
  }, [
    setFileProcessing,
    setDownloadStatus,
    fileApiCall,
    dispatch,
    commonText?.fileProcessing,
    commonText?.fileDownloadError,
  ])

  return downloadSignedDocument
}
